import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

import { dashboardCardDimensions, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import CarouselHeader from './CarouselHeader';

interface NewCarouselProps {
	title: string;
	onShowAll?: () => void;
	cardWidth?: number;
	loading?: boolean;
}

const NewCarousel: React.FC<NewCarouselProps> = (props) => {
	const { title, onShowAll, children, cardWidth = dashboardCardDimensions.width, loading } = props;
	const { isXsScreen } = useScreenSize();
	const containerRef = useRef<HTMLDivElement>(null);
	const sliderRef = useRef<Slider>(null);

	const [slidesToShow, setSlidesToShow] = useState(1.1);
	const cardGap = isXsScreen ? Number.parseInt(spaces.xs, 10) : Number.parseInt(spaces.md, 10);

	const calculateSlidesToShow = () => {
		if (containerRef.current) {
			const containerWidth = containerRef.current.offsetWidth;
			const totalCardWidth = cardWidth + cardGap;
			const visibleCards = containerWidth / totalCardWidth;
			const slides = Math.ceil(visibleCards * 100) / 100;
			setSlidesToShow(slides);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		calculateSlidesToShow();

		const handleResize = () => {
			calculateSlidesToShow();
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [cardWidth, cardGap]);

	const settings = {
		// navigation is custom via CarouselHeader; the slick theme css (icon font) is not shipped
		arrows: false,
		infinite: false,
		speed: 500,
		slidesToShow,
		slidesToScroll: Math.floor(slidesToShow),
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box sx={{ paddingRight: spaces.md }}>
				<CarouselHeader
					title={title}
					onSlideNext={() => sliderRef.current?.slickNext()}
					onSlidePrev={() => sliderRef.current?.slickPrev()}
					onShowAll={onShowAll}
					loading={loading}
				/>
			</Box>
			<Box
				ref={containerRef}
				data-testid="carousel"
				sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
					flexDirection: 'column',
					overflow: 'hidden',
					// slick centers a track narrower than the list via auto side-margins;
					// dashboard rows must stay left-aligned when they have few cards.
					'& .slick-track': { marginLeft: 0, marginRight: 0 },
					// react-slick wraps each slide child in an inline-block. Baseline-aligned
					// inline-blocks reserve a font-descender strut below the content, so the
					// slide renders a few px taller than the card/skeleton inside it — on
					// first paint the whole row (and everything below) shifts by that gap.
					// Top-align the wrapper so the slide height equals its content exactly.
					'& .slick-slide > div > *': { verticalAlign: 'top' },
				}}
			>
				<Slider ref={sliderRef} {...settings}>
					{children}
				</Slider>
			</Box>
		</Box>
	);
};

export default NewCarousel;
